/**
 * Regras de "responder mensagem" (citação), compartilhadas pelo chat web e pelo
 * mobile para não duplicar critério nem lógica de casamento de IDs.
 */

export interface ReplyableMessage {
  id?: string | number
  from_me?: boolean
  wa_message_id?: string | null
  text_content?: string | null
  media_kind?: string | null
  deleted_at?: string | null
  isOptimistic?: boolean
  isSending?: boolean
  quoted_wa_message_id?: string | null
  quoted_text_content?: string | null
  quoted_from_me?: boolean | null
}

/** Payload aceito pelo `quoted` da send-message-v3 (que vira `reply_to` na WAHA). */
export interface QuotedPayload {
  key: { id: string }
  message: { conversation: string }
  fromMe: boolean
}

export function canReplyToMessage(message: ReplyableMessage): boolean {
  if (message.deleted_at) return false
  if (message.isOptimistic || message.isSending) return false

  const waMessageId = message.wa_message_id
  return Boolean(waMessageId) && !waMessageId!.startsWith('optimistic_')
}

export function buildQuotedPayload(message: ReplyableMessage): QuotedPayload | null {
  if (!canReplyToMessage(message)) return null

  return {
    key: { id: message.wa_message_id! },
    // A WAHA ignora este campo (só usa o id); ele vira o snapshot da citação.
    message: { conversation: message.text_content ?? '' },
    fromMe: message.from_me === true,
  }
}

/**
 * Índice de mensagens por ID, para resolver citações em O(1).
 *
 * Necessário porque os dois lados guardam formatos diferentes: o que enviamos
 * daqui é o `wa_message_id` serializado (`true_55…@c.us_3EB0…`) e o que chega
 * do WhatsApp é só o stanzaId (`3EB0…`). Indexamos as duas formas, então a
 * busca não precisa varrer a lista comparando sufixos.
 */
export function buildMessageIndex<T extends ReplyableMessage>(
  messages: readonly T[]
): Map<string, T> {
  const index = new Map<string, T>()

  for (const message of messages) {
    const waMessageId = message.wa_message_id
    if (!waMessageId || waMessageId.startsWith('optimistic_')) continue

    index.set(waMessageId, message)

    const suffix = waMessageId.slice(waMessageId.lastIndexOf('_') + 1)
    if (suffix && suffix !== waMessageId && !index.has(suffix)) {
      index.set(suffix, message)
    }
  }

  return index
}

export function lookupQuotedMessage<T extends ReplyableMessage>(
  index: Map<string, T>,
  quotedId: string | null | undefined
): T | null {
  if (!quotedId) return null

  const direct = index.get(quotedId)
  if (direct) return direct

  const suffix = quotedId.slice(quotedId.lastIndexOf('_') + 1)
  return (suffix && suffix !== quotedId ? index.get(suffix) : undefined) ?? null
}

export interface QuotedPreview<T> {
  text: string | null
  fromMe: boolean | null
  /** Tipo de mídia da original, para rotular citações sem texto ("Foto", "Áudio"…). */
  mediaKind: string | null
  /** A mensagem original, quando ela está carregada — permite rolar até ela. */
  original: T | null
}

/**
 * Dados a exibir no bloco de citação. Prefere a mensagem original carregada
 * (sempre atual) e cai no snapshot gravado no envio quando ela está fora da
 * página, é anterior à feature, ou foi apagada.
 */
export function resolveQuotedPreview<T extends ReplyableMessage>(
  message: ReplyableMessage,
  index: Map<string, T>
): QuotedPreview<T> | null {
  const quotedId = message.quoted_wa_message_id
  if (!quotedId) return null

  const original = lookupQuotedMessage(index, quotedId)

  const text = original && !original.deleted_at
    ? (original.text_content ?? null)
    : (message.quoted_text_content ?? null)

  const fromMe = original
    ? original.from_me === true
    : (typeof message.quoted_from_me === 'boolean' ? message.quoted_from_me : null)

  return { text, fromMe, mediaKind: original?.media_kind ?? null, original }
}
